// Copyright (C) 2022 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import axios, {
  AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse,
  AxiosResponseHeaders
} from 'axios'

import AnonymousUser from './AnonymousUser'
import AuthenticatedUser from './AuthenticatedUser'
import IUser from './IUser'


export default class GatewayClient {
  public gateway: AxiosInstance
  private oauth2: AxiosInstance

  constructor(params: {
    gatewayUrl?: string,
    oauthUrl?: string
  }) {
    this.gateway = axios.create({
      baseURL: params.gatewayUrl 
        || `${window.location.protocol}//${window.location.hostname}/api`
    })
    this.oauth2 = axios.create({
      baseURL: params.oauthUrl
        || `${window.location.protocol}//${window.location.hostname}/api/oauth/v2`
    })

    // Create an interceptor that responds to the headers returned by the API
    // gateway.
    const self = this
    this.gateway.interceptors.response.use(
      (response) => { return response },
      async (error: AxiosError) => {
        if (!error.isAxiosError) { throw error }

        // If the X-Refresh-URL is present, this is an indication by the
        // token handler that the client must refresh the access token
        // for the service it is attempting to access.
        const request = error.config as AxiosRequestConfig &  {_retry: boolean};
        const response = error.response as AxiosResponse
        const headers = response.headers as AxiosResponseHeaders
        if (response === undefined || request === undefined || !!request._retry) {
          return Promise.reject(error)
        }
        //@ts-ignore
        const refreshUrl = headers.get("x-refresh-url") as string
        if (!!refreshUrl) {
          await self.refreshService(refreshUrl)
          request._retry = true
          return this.gateway(request);
        }
        return Promise.reject(error)
      }
    )
  }

  async refreshService(url: string): Promise<void> {
    await this.oauth2.post(url)
  }

  async getCurrentUser(): Promise<IUser> {
    let user: IUser = new AnonymousUser()
    try {
      const response = await this.oauth2.get('user')
      user = new AuthenticatedUser(this, response.data)
    } catch(err: any) {
      if (!err.isAxiosError) { throw err }
      if (![401, 404].includes(err.response.status)) { throw err }
    }
    return user
  }

  async login(locale: string, redirectUri: string, params?: any): Promise<string> {
    localStorage.clear()
    sessionStorage.clear()
    const response = await this.oauth2.post('login', {
      ...(params || {}),
      ui_locales: [locale],
      scope: ["openid", "email", "profile"],
      redirect_uri: redirectUri
    })
    return response.data.redirect_to
  }

  async signout(): Promise<void> {
    await this.oauth2.delete('/user')
    localStorage.clear()
    sessionStorage.clear()
  }
}

const client: GatewayClient = new GatewayClient({})

export { client }